/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { getCustomerMovementDisplayLabel } from '../domain/customerMovementLabels'
import type { CustomerMovementRow } from '../domain/customerTypes';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
// `openSaleDetail` is lazy-required to break the sales↔customer require cycle.
// Metro resolves top-level imports eagerly, which creates a cycle:
// sales/public-api → (salesRepoMutations etc.) → customer/public-api → CustomerMovementHistoryModal → sales/public-api.
// Resolving at call time breaks the static cycle without violating module boundaries.
const openSaleDetail = (navigation: ReturnType<typeof useNavigation>, params: { saleId: string }) =>
  require('../../sales/public-api').openSaleDetail(navigation, params);
import { preserveCustomerDetailForShellNavigation } from '../screens/customerDetailPreserve';
import { ModalFilterSelectTrigger } from '../../shared/modals/ModalFilterSelectTrigger'
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';

export type CustomerMovementTypeOption = { value: string; label: string };

type Props = {
  visible: boolean;
  customerName: string;
  movements: CustomerMovementRow[];
  historyLoading: boolean;
  historyLoadingMore: boolean;
  historyHasMore: boolean;
  onLoadMore: () => void;
  onClose: () => void;
  actorLabel: (userId: string | null | undefined) => string;
  typeOptions: CustomerMovementTypeOption[];
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onApplyFilters: () => void;
  onExportCsv: () => void;
  formatMoney: (n: number) => string;
  /** Abre el detalle del movimiento (comprobantes, datos del cheque). */
  onPressMovement: (movement: CustomerMovementRow) => void;
};

export const CustomerMovementHistoryModal = ({
  visible,
  customerName,
  movements,
  historyLoading,
  historyLoadingMore,
  historyHasMore,
  onLoadMore,
  onClose,
  actorLabel,
  typeOptions,
  typeFilter,
  onTypeFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onApplyFilters,
  onExportCsv,
  formatMoney,
  onPressMovement,
}: Props) => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  const typeLabel = useMemo(() => {
    return typeOptions.find((o) => o.value === typeFilter)?.label ?? 'Todos';
  }, [typeFilter, typeOptions]);

  const hasActiveFilters = typeFilter !== 'all' || fromDate.trim().length > 0 || toDate.trim().length > 0;

  const header = (
    <TicketModalHeader
      kicker="Clientes"
      title="Historial de cuenta"
      subtitle={`${customerName} · ${movements.length} ${movements.length === 1 ? 'movimiento' : 'movimientos'}`}
      onClose={onClose}
      compact={!isDesktop}
    />
  );

  const listHeader = useMemo(
    () => (
      <View style={styles.listHeader}>
        <View style={modalExperience.filtersPanel}>
          <Text style={modalExperience.filtersPanelTitle}>Filtros</Text>
          <Text style={modalExperience.filterFieldLabel}>Rango de fechas</Text>
          <View style={modalExperience.filterDateRow}>
            <View style={modalExperience.filterDateCol}>
              <Text style={modalExperience.filterDateHint}>Desde</Text>
              <InventoryIsoDateField
                value={fromDate}
                onChange={onFromDateChange}
                emptyLabel="Sin fecha"
                ariaLabel="Fecha desde"
                clearButtonLabel="Quitar"
                iosEmptyBehavior="openPicker"
              />
            </View>
            <View style={modalExperience.filterDateCol}>
              <Text style={modalExperience.filterDateHint}>Hasta</Text>
              <InventoryIsoDateField
                value={toDate}
                onChange={onToDateChange}
                emptyLabel="Sin fecha"
                ariaLabel="Fecha hasta"
                clearButtonLabel="Quitar"
                iosEmptyBehavior="openPicker"
              />
            </View>
          </View>

          <ModalFilterSelectTrigger
            label="Tipo de movimiento"
            value={typeLabel}
            onPress={() => setTypePickerOpen(true)}
            active={hasActiveFilters}
            accessibilityLabel="Elegir tipo de movimiento"
          />

          <View style={modalExperience.filterActions}>
            <TouchableOpacity
              style={[posUi.primaryButton, modalExperience.filterActionFlex]}
              onPress={onApplyFilters}
            >
              <Text style={posUi.primaryButtonText}>Aplicar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[posUi.secondaryButton, modalExperience.filterActionFlex]}
              onPress={onExportCsv}
            >
              <Text style={posUi.secondaryButtonText}>Exportar CSV</Text>
            </TouchableOpacity>
          </View>
        </View>

        {historyLoading ? (
          <ModalStatePanel loading loadingMessage="Cargando movimientos…" />
        ) : movements.length === 0 ? (
          <ModalStatePanel
            empty
            emptyTitle="Sin movimientos"
            emptyMessage="Probá ampliar el rango de fechas o elegir otro tipo."
          />
        ) : null}
      </View>
    ),
    [
      customerName,
      fromDate,
      hasActiveFilters,
      historyLoading,
      movements.length,
      onApplyFilters,
      onExportCsv,
      onFromDateChange,
      onToDateChange,
      toDate,
      typeLabel,
    ]
  );

  const renderItem = useCallback(
    ({ item: m }: { item: CustomerMovementRow }) => {
      const isCharge = m.type === 'charge';
      return (
        <TouchableOpacity
          style={modalExperience.listRow}
          onPress={() => {
            if (m.saleId) return;
            onPressMovement(m);
          }}
          accessibilityRole="button"
          accessibilityLabel={`Ver detalle de ${getCustomerMovementDisplayLabel(m)}`}
        >
          <View style={styles.rowTop}>
            <View
              style={[
                styles.amountBadge,
                isCharge ? styles.amountBadgeNeg : styles.amountBadgePos,
              ]}
            >
              <Text
                style={[
                  styles.amountText,
                  isCharge ? styles.amountNeg : styles.amountPos,
                ]}
              >
                {formatMoney(m.amount)}
              </Text>
            </View>
            <View style={styles.rowMain}>
              <Text style={modalExperience.listRowTitle} numberOfLines={2}>
                {getCustomerMovementDisplayLabel(m)}
              </Text>
              <Text style={modalExperience.listMetaMuted}>
                {formatLocalDateTime(m.createdAt)}
              </Text>
            </View>
          </View>
          {m.saleId ? (
            <TouchableOpacity
              style={styles.viewSaleBtn}
              onPress={() => {
                if (!m.saleId) return;
                preserveCustomerDetailForShellNavigation();
                openSaleDetail(navigation, { saleId: m.saleId });
              }}
              accessibilityRole="button"
            >
              <Text style={modalExperience.accentLink}>Origen: venta POS</Text>
              <Ionicons name="chevron-forward" size={14} color={ticketColors.gold} />
            </TouchableOpacity>
          ) : null}
          {m.note ? (
            <Text style={modalExperience.listMeta} numberOfLines={3}>
              {m.note}
            </Text>
          ) : null}
          <Text style={modalExperience.listMeta} numberOfLines={2}>
            Usuario: {actorLabel(m.performedByUserId) || '—'}
            {m.originDeviceId ? ` · Equipo: ${m.originDeviceId}` : ''}
          </Text>
        </TouchableOpacity>
      );
    },
    [actorLabel, formatMoney, onPressMovement]
  );

  const listFooter = useMemo(() => {
    if (historyLoading || movements.length === 0) return null;
    return (
      <>
        {historyLoadingMore ? (
          <View style={modalExperience.footerLoading}>
            <ActivityIndicator color={ticketColors.gold} size="small" />
            <Text style={modalExperience.stateText}>Cargando más…</Text>
          </View>
        ) : null}
        {historyHasMore && !historyLoadingMore ? (
          <TouchableOpacity style={modalExperience.loadMoreBtn} onPress={onLoadMore}>
            <Text style={modalExperience.accentLink}>Cargar más movimientos</Text>
          </TouchableOpacity>
        ) : null}
      </>
    );
  }, [historyHasMore, historyLoading, historyLoadingMore, movements.length, onLoadMore]);

  if (isDesktop) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[styles.sheetBackdrop, { justifyContent: 'center', alignItems: 'center' }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View style={[styles.sheetCard, { maxWidth: 560, borderRadius: 16, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
            {header}

            {/* Un solo scroll: header (filtros) + movimientos juntos. */}
            <FlatList
              style={styles.sheetScroll}
              contentContainerStyle={styles.sheetScrollContent}
              data={historyLoading ? [] : movements}
              keyExtractor={(item) => item.id}
              ListHeaderComponent={listHeader}
              ListFooterComponent={listFooter}
              renderItem={renderItem}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
              onEndReached={() => {
                if (historyHasMore && !historyLoadingMore && !historyLoading) onLoadMore();
              }}
              onEndReachedThreshold={0.35}
            />
          </View>

          <ModalNestedPicker
            visible={typePickerOpen}
            title="Tipo de movimiento"
            options={typeOptions}
            selectedValue={typeFilter}
            onSelect={onTypeFilterChange}
            onClose={() => setTypePickerOpen(false)}
          />
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.sheetBackdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
          <View style={styles.grabber} />
          {header}

          {/* Un solo scroll: header (filtros) + movimientos juntos. */}
          <FlatList
            style={styles.sheetScroll}
            contentContainerStyle={styles.sheetScrollContent}
            data={historyLoading ? [] : movements}
            keyExtractor={(item) => item.id}
            ListHeaderComponent={listHeader}
            ListFooterComponent={listFooter}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            onEndReached={() => {
              if (historyHasMore && !historyLoadingMore && !historyLoading) onLoadMore();
            }}
            onEndReachedThreshold={0.35}
          />
        </View>

        <ModalNestedPicker
          visible={typePickerOpen}
          title="Tipo de movimiento"
          options={typeOptions}
          selectedValue={typeFilter}
          onSelect={onTypeFilterChange}
          onClose={() => setTypePickerOpen(false)}
        />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    maxHeight: '92%',
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.sm,
  },
  listHeader: {
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  amountBadge: {
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    minWidth: 88,
    alignItems: 'center',
  },
  amountBadgePos: {
    backgroundColor: 'rgba(74, 222, 128, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(74, 222, 128, 0.35)',
  },
  amountBadgeNeg: {
    backgroundColor: 'rgba(248, 113, 113, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(248, 113, 113, 0.35)',
  },
  amountText: {
    fontWeight: '800',
    fontSize: 15,
  },
  amountPos: { color: ticketColors.ok },
  amountNeg: { color: ticketColors.bad },
  viewSaleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
  },
});
